import { useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button } from '@/shared/ui'
import { useAdminCompanies } from '@/entities/admin'
import type { AdminCompanySort } from '@/shared/api/types'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { formatAdminDate, formatDecimal, formatNumber } from '../shared/format'
import styles from '../AdminPages.module.css'

const SORT_OPTIONS: { value: AdminCompanySort; label: string }[] = [
  { value: 'activity', label: 'По активности' },
  { value: 'calls', label: 'По звонкам' },
  { value: 'minutes', label: 'По минутам' },
  { value: 'created', label: 'По дате создания' },
]

export function AdminCompaniesPage() {
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState<AdminCompanySort>('activity')
  const companies = useAdminCompanies(query, sortBy)

  function submit(event: FormEvent) {
    event.preventDefault()
    setQuery(draft.trim())
  }

  return (
    <>
      <PageHeader
        title="Компании"
        description="Клиенты, их активность и рабочие настройки."
      />

      <form className={styles.toolbar} onSubmit={submit}>
        <label className={`${styles.field} ${styles.fieldGrow}`}>
          <span className={styles.label}>Название или почта владельца</span>
          <input
            className={styles.input}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Например, Альфа или owner@example.ru"
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Сортировка</span>
          <select
            className={styles.select}
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value as AdminCompanySort)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" variant="secondary">
          Найти
        </Button>
        {query && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft('')
              setQuery('')
            }}
          >
            Сбросить
          </Button>
        )}
        <span className={styles.count}>Найдено: {formatNumber(companies.data?.total ?? 0)}</span>
      </form>

      {companies.isPending ? (
        <AdminRemoteState kind="loading" />
      ) : companies.isError ? (
        <AdminRemoteState
          kind="error"
          message="Список компаний не загрузился."
          onRetry={() => void companies.refetch()}
        />
      ) : !companies.data?.items.length ? (
        <AdminRemoteState
          kind="empty"
          title={query ? 'Компании не найдены' : 'Компаний пока нет'}
          message={query ? 'Измените запрос или сбросьте поиск.' : 'Новые компании появятся здесь после регистрации.'}
        />
      ) : (
        <>
          <div className={`${styles.tableWrap} ${styles.desktopTable}`}>
            <table className={`${styles.table} ${styles.tableWide}`}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Компания</th>
                  <th>Владелец</th>
                  <th>Создана</th>
                  <th>Доступ</th>
                  <th>Последний звонок</th>
                  <th>Звонки за 30 дней</th>
                  <th>Минуты за 30 дней</th>
                  <th>Обзвоны</th>
                  <th>Модель синтеза</th>
                  <th>Голос</th>
                  <th>Диалоговая модель</th>
                </tr>
              </thead>
              <tbody>
                {companies.data.items.map((company) => (
                  <tr key={company.id}>
                    <td className={styles.mono}>{company.id}</td>
                    <td>
                      <Link className={styles.link} to={`/admin/companies/${company.id}`}>
                        {company.name}
                      </Link>
                    </td>
                    <td>{company.ownerEmail}</td>
                    <td>{formatAdminDate(company.createdAt)}</td>
                    <td>
                      <CompanyStatus blockedAt={company.blockedAt} />
                    </td>
                    <td>{formatAdminDate(company.lastCallAt)}</td>
                    <td>{formatNumber(company.calls30d)}</td>
                    <td>{formatDecimal(company.minutes30d)}</td>
                    <td>{formatNumber(company.campaigns)}</td>
                    <td className={styles.mono}>{company.ttsModel || 'По умолчанию'}</td>
                    <td className={styles.mono}>{company.voiceId || 'По умолчанию'}</td>
                    <td className={styles.mono}>{company.llmModel || 'По умолчанию'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileCards} aria-label="Список компаний">
            {companies.data.items.map((company) => (
              <article key={company.id} className={styles.mobileCard}>
                <CompanyRow label="№" value={String(company.id)} mono />
                <CompanyRow
                  label="Компания"
                  value={
                    <Link className={styles.link} to={`/admin/companies/${company.id}`}>
                      {company.name}
                    </Link>
                  }
                />
                <CompanyRow label="Статус" value={<CompanyStatus blockedAt={company.blockedAt} />} />
                <CompanyRow label="Создана" value={formatAdminDate(company.createdAt)} />
                <CompanyRow label="Последний звонок" value={formatAdminDate(company.lastCallAt)} />
                <CompanyRow label="Звонки · 30 дней" value={formatNumber(company.calls30d)} />
                <CompanyRow label="Минуты · 30 дней" value={formatDecimal(company.minutes30d)} />
                <CompanyRow label="Обзвоны" value={formatNumber(company.campaigns)} />
                <CompanyRow label="Владелец" value={company.ownerEmail} />
                <CompanyRow label="Синтез" value={company.ttsModel || 'По умолчанию'} mono />
                <CompanyRow label="Голос" value={company.voiceId || 'По умолчанию'} mono />
                <CompanyRow label="Модель" value={company.llmModel || 'По умолчанию'} mono />
              </article>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function CompanyStatus({ blockedAt }: { blockedAt: string | null }) {
  return (
    <span
      className={`${styles.status} ${blockedAt ? styles.statusDanger : styles.statusGood}`}
      title={blockedAt ? `Заблокирована ${formatAdminDate(blockedAt)}` : 'Компания может пользоваться кабинетом.'}
    >
      {blockedAt ? 'Заблокирована' : 'Работает'}
    </span>
  )
}

function CompanyRow({
  label,
  value,
  mono,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div className={styles.mobileRow}>
      <span className={styles.mobileLabel}>{label}</span>
      <span className={`${styles.mobileValue} ${mono ? styles.mono : ''}`} data-admin-value>
        {value}
      </span>
    </div>
  )
}
